import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMediaQuery, useTheme } from '@mui/material';

import { EMPTY_OBJECT } from '@e-socna/empty';

import { useColorToMuiColor } from '../hooks/useColorToMuiColor.hook';
import { iconToMuiIcon } from '../utils/icon-to-mui-icon.util';
import { FolderBusinessEntityStateForm } from '../forms/FolderBusinessEntityState';
import type { WorkflowNode } from '../forms/FolderBusinessEntityState/types/workflow-node.type';
import type { ActionPermission } from '../forms/FolderBusinessEntityState/types/find-all-action-permissions-query.type';
import type { UseUpdateFolderBusinessEntityStateSubmit } from '../forms/FolderBusinessEntityState/types/use-submit.type';
import type { RenderActionDialog } from '../forms/FolderBusinessEntityState/types/action-dialog-render-props.type';

import { Avatar } from './Avatar';
import { GatedButton } from './Access/GatedButton';
import { OPEN } from './Access/utils/access-gate.util';
import type { AccessGate } from './Access/types/access-gate.type';
import { RightDrawer } from './RightDrawer';

type PropsDependingOnEnableAction = Readonly<{
  actionPermissions: readonly ActionPermission[] | undefined;
  areActionPermissionsLoading: boolean;
  formTitle: string;
  isInEditionMode: boolean;
  onCancel: () => void;
  onEdit: () => void;
  renderActionDialog?: RenderActionDialog;
  useUpdateStateSubmit: UseUpdateFolderBusinessEntityStateSubmit;
}>;

type Props = Readonly<{
  currentNode: WorkflowNode;
  // Whether the user may change the state (an action of the current state open to them).
  gate?: AccessGate;
  showSummary?: boolean;
}> &
  (
    | (Readonly<{
        enableAction: false;
      }> &
        Partial<PropsDependingOnEnableAction>)
    | (Readonly<{
        enableAction: true;
      }> &
        PropsDependingOnEnableAction)
  );

export const FolderBusinessEntityState = ({
  actionPermissions,
  areActionPermissionsLoading,
  currentNode,
  enableAction,
  formTitle,
  gate = OPEN,
  isInEditionMode,
  onCancel: handleCancel,
  onEdit: handleEdit,
  renderActionDialog,
  showSummary = true,
  useUpdateStateSubmit,
}: Readonly<Props>) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('sm'));

  const { colorToMuiColor } = useColorToMuiColor();

  const updateButton = (
    <GatedButton
      disabled={!enableAction}
      gate={gate}
      onClick={handleEdit}
      sx={!matches ? { mt: 2 } : EMPTY_OBJECT}
      variant="outlined"
    >
      Mettre à jour
    </GatedButton>
  );

  return (
    <>
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
      {!!showSummary && (
        <Paper
          sx={{
            backgroundColor: colorToMuiColor(currentNode.chip.color),
            display: 'flex',
            flexDirection: 'row',
            p: 2,
          }}
        >
          <Stack direction="column" sx={{ display: 'flex', flexGrow: 1 }}>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                flexGrow: 1,
              }}
            >
              <Avatar
                bgColor={colorToMuiColor(currentNode.chip.color)}
                icon={iconToMuiIcon(currentNode.chip.icon)}
                label={`État : ${currentNode.label}`}
              />
              <Stack
                direction="column"
                spacing={1}
                sx={{ display: 'flex', flexGrow: 1, pl: 2 }}
              >
                <Typography gutterBottom={false} variant="h6">
                  Statut : {currentNode.label}
                </Typography>
                <Typography sx={{ mt: '0 !important' }} variant="body1">
                  Description : {currentNode.description}
                </Typography>
              </Stack>
              {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
              {!!matches && updateButton}
            </Box>
            {!matches && updateButton}
          </Stack>
        </Paper>
      )}
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
      {!!enableAction && (
        <RightDrawer
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion
          isOpen={!!isInEditionMode}
          onDrawerClose={handleCancel}
          title={formTitle}
        >
          <FolderBusinessEntityStateForm
            actionPermissions={actionPermissions}
            areActionPermissionsLoading={areActionPermissionsLoading}
            currentNode={currentNode}
            onCancel={handleCancel}
            renderActionDialog={renderActionDialog}
            useUpdateStateSubmit={useUpdateStateSubmit}
          />
        </RightDrawer>
      )}
    </>
  );
};
